import React, { useEffect, useId, useRef } from 'react';
import { CARE_CSS, CareIcon } from './CheckInCard';

/**
 * 🫂 THE CRISIS CARD (SPACES_REDESIGN_BRAINSTORM_2026_09_29 §5.12, mockup
 * room-toolkit.html §8, option A): "You don't have to carry this alone." A calm
 * card under the sender's own message, for that sender only.
 *
 * WHEN: the page opens it when THEIR interjection response carries
 * `care: 'crisis'` (backend careCrisis.js, dark behind CARE_CRISIS_CARD_ENABLED:
 * their OWN line scored self-harm/intent; never the room's dark topics, never an
 * AI line) and it has not shown yet this session: utils/careCheckIn.takeCrisisCard
 * is that one call. Their message still posts; nothing here is sent anywhere.
 *
 * PROPS: { open, onClose }. Not a modal (the chat stays usable): a labelled
 * region that takes focus when it opens (so a screen reader reads it), unless
 * the person is already typing their next line, and hands focus back where it
 * was on Close (or Escape).
 *
 * ⚠️ THE COPY NEEDS NINA'S REVIEW BEFORE THE FLAG FLIPS (CRISIS_COPY below).
 * Calm, not alarming; no em dashes. Not legal advice: whether CA SB 243 / NY GBL
 * Art. 47 apply is Nina's and a lawyer's question.
 * data-care-crisis = the grep/test marker.
 */

export const CRISIS_COPY = Object.freeze({
  title: 'You don’t have to carry this alone.',
  body: 'If you’re thinking about hurting yourself, you can talk to someone now. Free, private, 24/7.',
  lines: Object.freeze([
    Object.freeze({ key: '988', icon: 'phone', label: 'Call or text 988 (US)', sub: 'Suicide and Crisis Lifeline', href: 'tel:988' }),
    Object.freeze({ key: '741741', icon: 'text', label: 'Text HOME to 741741', sub: 'Crisis Text Line', href: 'sms:741741?&body=HOME' }),
    Object.freeze({ key: 'elsewhere', icon: 'globe', label: 'Elsewhere: findahelpline.com', sub: 'A line in your country', href: 'https://findahelpline.com', external: true }),
  ]),
  aiNote: 'The characters here are AI. They can’t help in an emergency. If you’re in danger, call 911.',
  only: 'Only you see this.',
  close: 'Close',
});

// The response lands AFTER the send, so the person may already be writing their
// next line (review 09-29): pulling focus then would swallow what they type,
// at the worst moment. The card still shows; it just doesn't take focus.
function isTypingIn(el) {
  try {
    if (!el) return false;
    const tag = String(el.tagName || '').toLowerCase();
    if (tag === 'textarea' || (tag === 'input' && /^(text|search|email|url|tel)$/i.test(String(el.type || 'text')))) {
      return String(el.value || '').trim() !== '';
    }
    return !!el.isContentEditable && String(el.textContent || '').trim() !== '';
  } catch (e) { return false; }
}

export default function CrisisCard({ open, onClose }) {
  const rootRef = useRef(null);
  const backTo = useRef(null);
  const titleId = `cck-crisis-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;

  useEffect(() => {
    if (!open) return;
    try {
      backTo.current = typeof document !== 'undefined' ? document.activeElement : null;
      if (rootRef.current && !isTypingIn(backTo.current)) rootRef.current.focus({ preventScroll: false });
    } catch (e) { /* focus is a courtesy */ }
  }, [open]);

  if (!open) return null;

  const close = () => {
    const el = backTo.current;
    if (typeof onClose === 'function') onClose();
    try {
      if (el && el !== document.body && typeof el.focus === 'function' && document.contains(el)) el.focus({ preventScroll: true });
    } catch (e) { /* the element went away */ }
  };

  return (
    <div ref={rootRef} className="cck-root" role="region" aria-labelledby={titleId} tabIndex={-1} data-care-crisis="open"
      onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); close(); } }}>
      <style>{CARE_CSS}</style>
      <div className="cck-hd">
        <span className="cck-ico"><CareIcon name="heart" /></span>
        <p className="cck-ttl" id={titleId}>{CRISIS_COPY.title}</p>
      </div>
      <p className="cck-bd" style={{ marginBottom: 0 }}>{CRISIS_COPY.body}</p>
      <div className="cck-res-list">
        {CRISIS_COPY.lines.map((l) => (
          <a key={l.key} className="cck-res" data-care-line={l.key} href={l.href}
            {...(l.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
            <span className="cck-ri"><CareIcon name={l.icon} /></span>
            <span><b>{l.label}</b><small>{l.sub}</small></span>
          </a>
        ))}
      </div>
      <p className="cck-note" style={{ margin: 0 }}><CareIcon name="info" size={15} /><span>{CRISIS_COPY.aiNote}</span></p>
      <div className="cck-ft">
        <CareIcon name="lock" size={12} stroke={2.2} />
        <span>{CRISIS_COPY.only}</span>
        <span className="cck-sp" />
        <button type="button" data-prop-ui="" className="cck-opt" data-care-close="1" onClick={close}>{CRISIS_COPY.close}</button>
      </div>
    </div>
  );
}
